import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Check, Loader2 } from 'lucide-react';
import { trackEvent } from '../../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LeadForm({ source, idPrefix, buttonLabel = 'JE COMMENCE MON ÉCLORA', footnote }) {
    const navigate = useNavigate();
    const [prenom, setPrenom] = useState('');
    const [email, setEmail] = useState('');
    const [consent, setConsent] = useState(false);
    const [newsletter, setNewsletter] = useState(false);
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState('');

    const submit = async (e) => {
        e.preventDefault();
        if (!prenom.trim() || !email.trim()) {
            setError('Merci de renseigner ton prénom et ton adresse email.');
            return;
        }
        if (!consent) {
            setError('Merci de cocher la case de consentement pour recevoir ton workbook.');
            return;
        }
        setError('');
        setStatus('loading');
        try {
            await axios.post(`${API}/api/leads`, { prenom: prenom.trim(), email: email.trim(), consent, newsletter, source });
            trackEvent('lead_submit', { source, newsletter });
            navigate('/confirmation', { state: { prenom: prenom.trim() } });
        } catch (err) {
            setStatus('idle');
            setError(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Une erreur est survenue. Réessaie dans un instant.");
        }
    };

    return (
        <form onSubmit={submit} noValidate className="w-full" data-testid={`${idPrefix}-form`}>
            <div className="space-y-3">
                <div>
                    <label htmlFor={`${idPrefix}-prenom`} className="sr-only">Prénom</label>
                    <input
                        id={`${idPrefix}-prenom`}
                        type="text"
                        autoComplete="given-name"
                        placeholder="Ton prénom"
                        value={prenom}
                        onChange={(e) => setPrenom(e.target.value)}
                        className="field-input"
                        data-testid={`${idPrefix}-prenom-input`}
                    />
                </div>
                <div>
                    <label htmlFor={`${idPrefix}-email`} className="sr-only">Adresse email</label>
                    <input
                        id={`${idPrefix}-email`}
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        placeholder="Ton adresse email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="field-input"
                        data-testid={`${idPrefix}-email-input`}
                    />
                </div>
            </div>

            <div className="mt-4 space-y-3">
                <div className="flex items-start gap-3">
                    <button
                        type="button"
                        role="checkbox"
                        aria-checked={consent}
                        aria-label="Consentement RGPD"
                        onClick={() => setConsent(!consent)}
                        data-testid={`${idPrefix}-consent-checkbox`}
                        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border transition-colors duration-300 ${
                            consent ? 'border-bordeaux bg-bordeaux text-creme' : 'border-champagne/60 bg-white/70 text-transparent'
                        }`}
                    >
                        <Check className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                    <p className="text-left text-xs leading-relaxed text-cacao/70">
                        J'accepte de recevoir gratuitement Reset Éclora par email.{' '}
                        <a
                            href="/politique-de-confidentialite"
                            className="underline decoration-champagne/60 underline-offset-2 transition-colors duration-300 hover:text-bordeaux"
                            data-testid={`${idPrefix}-privacy-link`}
                        >
                            Politique de confidentialité
                        </a>
                    </p>
                </div>
                <div className="flex items-start gap-3">
                    <button
                        type="button"
                        role="checkbox"
                        aria-checked={newsletter}
                        aria-label="Inscription à La Lettre Éclora (facultatif)"
                        onClick={() => setNewsletter(!newsletter)}
                        data-testid={`${idPrefix}-newsletter-checkbox`}
                        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border transition-colors duration-300 ${
                            newsletter ? 'border-bordeaux bg-bordeaux text-creme' : 'border-champagne/60 bg-white/70 text-transparent'
                        }`}
                    >
                        <Check className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                    <p className="text-left text-xs leading-relaxed text-cacao/70">
                        Je souhaite également recevoir La Lettre Éclora et les communications de Mon Éclora (facultatif, désinscription possible à tout moment).
                    </p>
                </div>
            </div>

            {error && (
                <p className="mt-3 text-left text-xs text-bordeaux" role="alert" data-testid={`${idPrefix}-error`}>
                    {error}
                </p>
            )}

            <button type="submit" disabled={status === 'loading'} className="btn-bordeaux group mt-5" data-testid={`${idPrefix}-submit-button`}>
                {status === 'loading' && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                <span className="relative z-10">{buttonLabel}</span>
                <span
                    aria-hidden="true"
                    className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full"
                />
            </button>

            {footnote && (
                <p className="mt-3 text-center text-xs text-cacao/60" data-testid={`${idPrefix}-footnote`}>
                    {footnote}
                </p>
            )}
        </form>
    );
}
